<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Rapport de Commission par Serveur</h1>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Filters -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="6" md="3">
              <v-select :items="stores" item-value="id" item-text="name" placeholder="Emplacement" v-model="store_id" outlined dense hide-details clearable></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select :items="serverFilterOptions" item-value="id" item-text="name" placeholder="Serveur" v-model="server_id" outlined dense hide-details clearable></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="auto">
              <div class="d-flex gap-2">
                <v-btn color="primary" depressed small @click="filter" :loading="loading" class="mr-1"><v-icon small>mdi-check</v-icon></v-btn>
                <v-btn outlined small @click="reset" :loading="loading" class="mr-1"><v-icon small>mdi-refresh</v-icon></v-btn>
                <v-btn color="error" outlined small @click="exportPDF" class="mr-1"><v-icon small>mdi-file-pdf-box</v-icon></v-btn>
                <v-btn color="success" outlined small @click="exportExcel"><v-icon small>mdi-file-excel</v-icon></v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table">
            <thead>
              <tr>
                <th class="text-left">SERVEUR</th>
                <th class="text-center">COMMANDES</th>
                <th class="text-right">VENTES HTG</th>
                <th class="text-right">VENTES USD</th>
                <th class="text-right">SERVICE HTG</th>
                <th class="text-right">SERVICE USD</th>
                <th class="text-right">COMMISSION ({{ currencyLabel }})</th>
              </tr>
              <tr class="totals-row">
                <th class="text-left">TOTAL ({{ rows.length }} serveurs)</th>
                <th class="text-center">{{ totals.orders }}</th>
                <th class="text-right">{{ totals.sales_htg | formatNumber }}</th>
                <th class="text-right">{{ totals.sales_usd | formatNumber }}</th>
                <th class="text-right">{{ totals.service_htg | formatNumber }}</th>
                <th class="text-right">{{ totals.service_usd | formatNumber }}</th>
                <th class="text-right">{{ totals.commission | formatNumber }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, idx) in rows" :key="idx">
                <td class="text-left font-weight-medium">{{ s.name }}</td>
                <td class="text-center">{{ s.orders }}</td>
                <td class="text-right">{{ s.sales_htg | formatNumber }}</td>
                <td class="text-right">{{ s.sales_usd | formatNumber }}</td>
                <td class="text-right">{{ s.service_htg | formatNumber }}</td>
                <td class="text-right">{{ s.service_usd | formatNumber }}</td>
                <td class="text-right">
                  <v-chip x-small color="success" dark>{{ s.commission | formatNumber }}</v-chip>
                </td>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="7" class="text-center text--secondary pa-6">Aucune donnée pour cette période</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'
import { readUsdContext } from '../utils/usdPersistence'

export default {
  name: 'ServersReportView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Restaurant', disabled: true }, { text: 'Commissions', disabled: false, href: '/restaurant/commissions' }],
    rows: [],
    enable: false,
    loading: false,
    filter_rate_id: 1,
    store_id: '',
    server_id: '',
    business_id: 0,
    stores: [],
    servers: [],
    orders: [],
  }),

  computed: {
    currencyLabel() { return Number(this.filter_rate_id) === 2 ? 'USD' : 'HTG' },
    serverFilterOptions() {
      return this.servers.map(function (s) { return { id: s.id, name: s.name } })
    },
    totals() {
      var t = { orders: 0, sales_htg: 0, sales_usd: 0, service_htg: 0, service_usd: 0, commission: 0 }
      this.rows.forEach(function (r) {
        t.orders += r.orders
        t.sales_htg += r.sales_htg
        t.sales_usd += r.sales_usd
        t.service_htg += r.service_htg
        t.service_usd += r.service_usd
        t.commission += r.commission
      })
      return t
    }
  },

  methods: {
    getDateParams() {
      var isValid = function (v) { return v && v !== 'null' && String(v).trim() !== '' }
      return {
        from: isValid(localStorage.debut_periode) ? localStorage.debut_periode : '',
        to: isValid(localStorage.fin_periode) ? localStorage.fin_periode : ''
      }
    },

    convertCommission(htg, usd) {
      var ctx = readUsdContext()
      var rate = ctx.rate > 0 ? ctx.rate : 0
      if (Number(this.filter_rate_id) === 2) {
        // Target USD: convert HTG to USD if rate available, else return USD only
        return usd + (rate > 0 ? htg / rate : 0)
      }
      // Target HTG: convert USD to HTG if rate available, else return HTG only
      return htg + (rate > 0 ? usd * rate : 0)
    },

    parseOrders(orders) {
      var self = this
      var byServer = {}

      orders.forEach(function (o) {
        var status = (o.status || '').toLowerCase()
        if (status !== 'closed') return
        var serverId = o.server_id || (o.server && o.server.id) || (o.server_details && o.server_details.id)
        if (!serverId) return
        if (self.server_id && Number(self.server_id) !== Number(serverId)) return

        var key = String(serverId)
        if (!byServer[key]) {
          var name = (o.server && o.server.name) || (o.server_details && o.server_details.name) || ''
          if (!name) {
            var match = self.servers.find(function (sv) { return Number(sv.id) === Number(serverId) })
            name = match ? match.name : 'Serveur #' + serverId
          }
          byServer[key] = { id: serverId, name: name, orders: 0, sales_htg: 0, sales_usd: 0, service_htg: 0, service_usd: 0, commission: 0 }
        }

        var rid = Number(o.rate_id) === 2 ? 2 : 1
        var total = parseFloat(o.total) || 0
        var fee = parseFloat(o.service_fee_amount) || 0

        byServer[key].orders += 1
        if (rid === 2) {
          byServer[key].sales_usd += total
          byServer[key].service_usd += fee
        } else {
          byServer[key].sales_htg += total
          byServer[key].service_htg += fee
        }
      })

      this.rows = Object.keys(byServer).map(function (k) {
        var r = byServer[k]
        r.commission = self.convertCommission(r.service_htg, r.service_usd)
        return r
      }).sort(function (a, b) { return b.commission - a.commission })
    },

    buildOrdersUrl() {
      var d = this.getDateParams()
      var params = ['include=rate,server', 'status=closed']
      if (d.from) params.push('from=' + d.from)
      if (d.to) params.push('to=' + d.to)
      if (this.store_id) params.push('store_id=' + this.store_id)
      if (this.server_id) params.push('server_id=' + this.server_id)
      return '/orders?' + params.join('&')
    },

    load() {
      var self = this
      this.enable = false
      this.$axios.get(this.buildOrdersUrl())
        .then(function (r) { self.orders = r.data || []; self.parseOrders(self.orders); self.enable = true })
        .catch(function () { self.enable = true })
    },

    filter() {
      var self = this
      self.loading = true
      this.$axios.get(this.buildOrdersUrl())
        .then(function (r) { self.orders = r.data || []; self.parseOrders(self.orders); self.loading = false })
        .catch(function () { self.loading = false })
    },

    reset() { this.store_id = ''; this.server_id = ''; this.filter() },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('l', 'mm', 'a4')
      var pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var d = self.getDateParams()
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], light = [248, 250, 252], border = [220, 225, 230]

      function fmt(v) { if (!v || isNaN(v)) return '0.00'; var p = Math.abs(parseFloat(v)).toFixed(2).split('.'); p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' '); return (parseFloat(v) < 0 ? '-' : '') + p.join('.') }

      doc.setFillColor(...primary); doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('RAPPORT DE COMMISSION PAR SERVEUR', ml, 11)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      doc.text(d.from + ' — ' + d.to, mr, 8, { align: 'right' })
      if (biz.name) doc.text(biz.name, mr, 13, { align: 'right' })

      var y = 24
      var cols = [
        { label: 'SERVEUR', w: 46, align: 'left' },
        { label: 'COMMANDES', w: 18, align: 'center' },
        { label: 'VENTES HTG', w: 26, align: 'right' },
        { label: 'VENTES USD', w: 26, align: 'right' },
        { label: 'SERVICE HTG', w: 26, align: 'right' },
        { label: 'SERVICE USD', w: 26, align: 'right' },
        { label: 'COMMISSION (' + self.currencyLabel + ')', w: 32, align: 'right' },
      ]
      var totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
      var scale = cw / totalW; cols.forEach(function (c) { c.w = Math.round(c.w * scale) })
      var actualW = cols.reduce(function (s, c) { return s + c.w }, 0); cols[0].w += (cw - actualW)
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 6.5

      function drawHeader() {
        doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
        cols.forEach(function (c) { var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3); doc.text(c.label, tx, y + 4.5, { align: c.align }) })
        y += rowH + 1
      }
      drawHeader()

      self.rows.forEach(function (row, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)
        var vals = [row.name, String(row.orders), fmt(row.sales_htg), fmt(row.sales_usd), fmt(row.service_htg), fmt(row.service_usd), fmt(row.commission)]
        cols.forEach(function (c, i) { var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3); doc.text(vals[i], tx, y + 4.2, { align: c.align }) })
        y += rowH
      })

      // Totals
      y += 1; doc.setFillColor(...light); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setDrawColor(...primary); doc.setLineWidth(0.4); doc.line(ml, y, mr, y)
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...dark)
      var t = self.totals
      doc.text('TOTAL (' + self.rows.length + ' serveurs)', ml + 3, y + 4.8)
      doc.text(String(t.orders), cols[1].x + cols[1].w / 2, y + 4.8, { align: 'center' })
      doc.text(fmt(t.sales_htg), cols[2].x + cols[2].w - 3, y + 4.8, { align: 'right' })
      doc.text(fmt(t.sales_usd), cols[3].x + cols[3].w - 3, y + 4.8, { align: 'right' })
      doc.text(fmt(t.service_htg), cols[4].x + cols[4].w - 3, y + 4.8, { align: 'right' })
      doc.text(fmt(t.service_usd), cols[5].x + cols[5].w - 3, y + 4.8, { align: 'right' })
      doc.text(fmt(t.commission), cols[6].x + cols[6].w - 3, y + 4.8, { align: 'right' })

      var totalPages = doc.internal.getNumberOfPages()
      for (var p = 1; p <= totalPages; p++) {
        doc.setPage(p); doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted); doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('rapport_commissions_serveurs.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var d = self.getDateParams()
      var wsData = [['RAPPORT DE COMMISSION PAR SERVEUR'], [biz.name || ''], ['Du ' + d.from + ' au ' + d.to], []]
      var headers = ['Serveur', 'Commandes', 'Ventes HTG', 'Ventes USD', 'Service HTG', 'Service USD', 'Commission (' + self.currencyLabel + ')']
      wsData.push(headers)
      self.rows.forEach(function (r) {
        wsData.push([
          r.name,
          r.orders,
          Math.round(r.sales_htg * 100) / 100,
          Math.round(r.sales_usd * 100) / 100,
          Math.round(r.service_htg * 100) / 100,
          Math.round(r.service_usd * 100) / 100,
          Math.round(r.commission * 100) / 100,
        ])
      })
      var t = self.totals
      wsData.push([])
      wsData.push([
        'TOTAL (' + self.rows.length + ' serveurs)',
        t.orders,
        Math.round(t.sales_htg * 100) / 100,
        Math.round(t.sales_usd * 100) / 100,
        Math.round(t.service_htg * 100) / 100,
        Math.round(t.service_usd * 100) / 100,
        Math.round(t.commission * 100) / 100,
      ])

      var wb = XLSX.utils.book_new(); var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 24 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 20 }]
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 4, c: 0 }, e: { r: 4, c: headers.length - 1 } }) }
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }, { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } }]
      for (var r = 5; r < wsData.length; r++) { for (var c = 2; c <= 6; c++) { var cell = ws[XLSX.utils.encode_cell({ r: r, c: c })]; if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = '#,##0.00' } } }
      XLSX.utils.book_append_sheet(wb, ws, 'Commissions'); XLSX.writeFile(wb, 'rapport_commissions_serveurs.xlsx')
    },

    async getStores() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      await this.$axios.get('/stores?orderasc=name&business_id=' + this.business_id).then(function (r) { self.stores = r.data })
    },

    async getServers() {
      var self = this
      await this.$axios.get('/servers?orderasc=name').then(function (r) { self.servers = r.data || [] }).catch(function () {})
    },
  },

  async created() {
    this.filter_rate_id = localStorage.filter_rate_id || 1
    await this.getServers()
    await this.getStores()
    this.load()
  },
}
</script>

<style scoped>
.filter-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-responsive { overflow-x: auto; max-height: 600px; overflow-y: auto; }
.report-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.report-table thead th { position: sticky; top: 0; background: #f8fafc; font-weight: 600; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 12px; border-bottom: 2px solid #e8ecf1; white-space: nowrap; z-index: 1; }
.report-table .totals-row th { background: #f1f5f9; color: #1a2332; font-weight: 700; font-size: 0.75rem; border-bottom: 2px solid #cbd5e1; }
.report-table tbody td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
.report-table tbody tr:hover { background-color: rgba(25, 65, 115, 0.03); }
.gap-2 { gap: 4px; }
</style>
